// Global Instructions Rule Applied!
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { updateTask } from '../../../store/slices/tasksSlice'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import { setPipelineSelectedFund } from '../../../state_manager/state_update_actions/fundraising/index.ts'
import { ProspectFields } from '../utils/fieldConfigs.ts'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import type { ProspectRecord } from '../../../state_manager/interfaces/fundraising.ts'

export const useFundraisingPipelineDrawer = (pipelineProspects: ProspectRecord[]) => {
  const { openDrawerForNew, openDrawerForRecord } = useDrawer()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const handleOpenDrawerForNew = useCallback(() => {
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForProspect = useCallback(
    (prospectId: string | number) => {
      const idStr = String(prospectId)
      const prospect = pipelineProspects.find(
        (p) =>
          p.id?.toString() === idStr ||
          (p as any)[ProspectRecordFields.prospects__id]?.toString() === idStr
      )
      const fundId = prospect?.[ProspectFields.prospects__fund] as string | undefined
      if (fundId) {
        dispatch(setPipelineSelectedFund({ selectedFundId: fundId }))
      }
      openDrawerForRecord(prospectId)
    },
    [dispatch, openDrawerForRecord, pipelineProspects]
  )

  const handleTasksClick = useCallback(
    (tasks: any[], prospectRecord: any, taskId: string | null = null) => {
      const prospectId = prospectRecord?.id

      if (prospectId) {
        const params = new URLSearchParams()
        if (taskId) {
          params.set('taskId', taskId)
        }
        const queryString = params.toString()
        navigate(`/fundraising/pipeline/forms/drawer/${prospectId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  const handleTaskToggle = useCallback(
    async (taskId: string, prospectId: string, completed: boolean) => {
      try {
        const prospect = pipelineProspects.find(
          (p) => p.id?.toString() === prospectId || (p as any)[ProspectRecordFields.prospects__id]?.toString() === prospectId
        )
        const prospectTasks = (prospect?.[ProspectRecordFields.prog__tasks] as any[]) || []
        const existingTask = prospectTasks.find(
          (t: any) => t[TaskRecordFields.tasks__id]?.toString() === taskId.toString()
        )

        if (!existingTask) {
          console.error('Task not found in prospect record')
          return
        }

        const completeTaskData = {
          ...existingTask,
          [TaskRecordFields.tasks__id]: taskId,
          [TaskRecordFields.tasks__completed]: completed
        }

        delete (completeTaskData as any)['task_deals__deal_id']
        delete (completeTaskData as any)['task_deals__id']
        delete (completeTaskData as any)['task_accounts__account_id']
        delete (completeTaskData as any)['task_accounts__id']
        delete (completeTaskData as any)['task_prospects__prospect_id']
        delete (completeTaskData as any)['task_prospects__id']

        await dispatch(
          updateTask({
            dealId: prospectId,
            taskId,
            taskData: completeTaskData,
            recordType: 'prospect'
          })
        ).unwrap()
      } catch (error) {
        console.error('Failed to update task:', error)
      }
    },
    [dispatch, pipelineProspects]
  )

  return {
    handleOpenDrawerForNew,
    handleOpenDrawerForProspect,
    handleTasksClick,
    handleTaskToggle
  }
}
